import { useLayoutEffect, useRef } from 'react';
import lottie from 'lottie-web';

const registerEvents = (anim, eventListeners) => {
  eventListeners.forEach((eventListener) => {
    anim.addEventListener(eventListener.eventName, eventListener.callback);
  });
};

const unregisterEvents = (anim, eventListeners) => {
  eventListeners.forEach((eventListener) => {
    anim.removeEventListener(eventListener.eventName, eventListener.callback);
  });
};

/**
 * A React Hook to initialize a Lottie animation json.
 * Takes a standard set of lottie options (https://airbnb.io/lottie/#/web?id=usage) as well as
 * an `initSegment` option that will jump to that segement and play.
 *
 * Returns both the lottie animation instance and the animation container.
 *
 * @param {Object} options Options to pass into lottie (see https://airbnb.io/lottie/#/web?id=usage)
 *
 * @returns [Object, Object]
 */

const useLottie = (options, optionalRef) => {
  const ref = useRef(null);
  const container = optionalRef ?? ref;
  const animationInstance = useRef(null);

  useLayoutEffect(() => {
    if (!container) {
      return null;
    }
    // set the lottie options
    const lottieOptions = { ...options, container: container.current };
    animationInstance.current = lottie.loadAnimation(lottieOptions);
    const events = options.events || [];

    // play initial segment if provided
    const playInitSegments = () => {
      if (options.initSegment) {
        animationInstance.current.playSegments(options.initSegment, true);
      }
    };
    animationInstance.current.addEventListener('DOMLoaded', playInitSegments);
    registerEvents(animationInstance.current, events);

    container.current.setAttribute('aria-hidden', true);

    return () => {
      animationInstance.current.removeEventListener('DOMLoaded', playInitSegments);
      unregisterEvents(animationInstance.current, events);
      animationInstance.current.destroy();
    };
  },
  // should be triggered on container change only
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [container],
  );

  return [animationInstance.current, container];
};

export default useLottie;
